import { Plus, Trash2 } from 'lucide-react';
import type { ClassPerfSyllabusResponse, ClassPerfSyllabusWeek } from '../../../api/types';
import { Badge, Button, IconButton } from '../../../components/ui';
import { dayMonth } from '../lib/cpBits';

interface Props {
  data: ClassPerfSyllabusResponse;
  canWrite: boolean;
  onAddEntry: (week: ClassPerfSyllabusWeek) => void;
  onDeleteEntry: (entryId: number) => void;
}

function sourceBadge(source: string) {
  if (source === 'teacher')  return <Badge tone="success" size="sm">Teacher-declared</Badge>;
  if (source === 'inferred') return <Badge tone="info" size="sm">Inferred from study plans</Badge>;
  return <Badge tone="neu" size="sm">No entries</Badge>;
}

/** Design CP `.wk`: one block per week inside the details card, separated by hairlines. */
export default function WeeklySyllabusList({ data, canWrite, onAddEntry, onDeleteEntry }: Props) {
  if (data.weeks.length === 0) {
    return <p className="text-sm text-muted">No weeks in window.</p>;
  }
  return (
    <div>
      {data.weeks.map((w) => (
        <section key={w.week_start} className="border-b border-hairline py-5 first:pt-0 last:border-b-0 last:pb-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="font-display text-[15px] font-bold tracking-[-0.01em] text-ink">Week of {dayMonth(w.week_start)}</h3>
            {sourceBadge(w.source)}
            <span className="flex-1" />
            {canWrite && (
              <Button size="sm" variant="ghost" icon={<Plus size={18} strokeWidth={1.8} aria-hidden />} onClick={() => onAddEntry(w)}>Add entry</Button>
            )}
          </div>
          {w.entries.length === 0 ? (
            <p className="mt-2.5 text-[13px] text-muted">No entries for this week yet.</p>
          ) : (
            <ul>
              {w.entries.map((e, idx) => {
                const detail = [e.notes, e.taught_by_name ?? (w.source === 'inferred' ? 'inferred' : '')].filter(Boolean).join(' · ');
                return (
                  <li key={e.id ?? `${w.week_start}-${idx}`}
                      className="mt-2.5 flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-[12px] bg-panel px-3.5 py-3">
                    <Badge tone="info" size="sm">{e.subject ?? '—'}</Badge>
                    <span className="min-w-0 flex-[1_1_240px]">
                      <span className="block font-semibold">{e.chapter}</span>
                      {detail && <span className="text-xs text-muted">{detail}</span>}
                    </span>
                    {canWrite && e.id != null && (
                      <IconButton tone="danger" label={`Delete ${e.chapter}`} onClick={() => onDeleteEntry(e.id!)}
                        icon={<Trash2 size={16} strokeWidth={1.8} aria-hidden />} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
